import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { academicApi } from '@/services/academic.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';
import { PersonPicker } from '@/modules/people/person-picker';
import { PersonTabPanel, PersonTabs } from '@/modules/people/person-detail-tabs';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * One School's pensum, teachers and course sections — everything scoped
 * under it, three tabs. Local component state for the active tab, the same
 * choice `person-detail-page.tsx` makes for the same reason.
 */
export function SchoolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const schoolId = id!;
  const { t } = useI18n();
  const { can } = useAuth();
  const [tab, setTab] = useState('pensum');

  const school = useQuery({
    queryKey: ['academic', 'schools', schoolId],
    queryFn: ({ signal }) => academicApi.getSchool(schoolId, signal),
  });

  if (school.isPending) return <Spinner label={t('common.states.loading')} />;
  if (school.isError) {
    return <Alert tone="danger">{errorMessage(school.error, t)}</Alert>;
  }

  const data = school.data;

  return (
    <div className="page">
      <Link to="/academic/schools" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{data.name}</h1>
          {data.description && <p className="page-subtitle">{data.description}</p>}
        </div>
      </header>

      <PersonTabs
        label={t('academic.school.title')}
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'pensum', label: t('academic.subject.pensumTab') },
          { id: 'teachers', label: t('academic.teacher.title') },
          { id: 'sections', label: t('academic.courseSection.title') },
        ]}
      />

      <PersonTabPanel id="pensum" active={tab}>
        <PensumTab schoolId={schoolId} canManage={can('academic.subject.create')} />
      </PersonTabPanel>
      <PersonTabPanel id="teachers" active={tab}>
        <TeachersTab schoolId={schoolId} canManage={can('academic.teacher.create')} />
      </PersonTabPanel>
      <PersonTabPanel id="sections" active={tab}>
        <SectionsTab schoolId={schoolId} canManage={can('academic.courseSection.create')} />
      </PersonTabPanel>
    </div>
  );
}

// --- Pensum: levels + subjects -----------------------------------------------------

function PensumTab({ schoolId, canManage }: { schoolId: string; canManage: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const levels = useQuery({
    queryKey: ['academic', 'levels', schoolId],
    queryFn: ({ signal }) => academicApi.listLevels(schoolId, false, signal),
  });
  const subjects = useQuery({
    queryKey: ['academic', 'subjects', schoolId],
    queryFn: ({ signal }) => academicApi.listSubjects(schoolId, false, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['academic', 'levels', schoolId] });
    void queryClient.invalidateQueries({ queryKey: ['academic', 'subjects', schoolId] });
  };

  const [levelName, setLevelName] = useState('');
  const [levelOrder, setLevelOrder] = useState('0');
  const [levelError, setLevelError] = useState<string | null>(null);
  const createLevel = useMutation({
    mutationFn: () =>
      academicApi.createLevel(schoolId, { name: levelName.trim(), order: Number(levelOrder) || 0 }),
    onSuccess: () => {
      setLevelName('');
      setLevelOrder('0');
      setLevelError(null);
      invalidate();
    },
    onError: (err: unknown) => setLevelError(errorMessage(err, t)),
  });

  const [subjectName, setSubjectName] = useState('');
  const [subjectLevelId, setSubjectLevelId] = useState('');
  const [subjectCredits, setSubjectCredits] = useState('');
  const [prerequisiteId, setPrerequisiteId] = useState('');
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const createSubject = useMutation({
    mutationFn: () =>
      academicApi.createSubject(schoolId, {
        levelId: subjectLevelId,
        name: subjectName.trim(),
        credits: subjectCredits ? Number(subjectCredits) : null,
        prerequisiteSubjectId: prerequisiteId || null,
      }),
    onSuccess: () => {
      setSubjectName('');
      setSubjectCredits('');
      setPrerequisiteId('');
      setSubjectError(null);
      invalidate();
    },
    onError: (err: unknown) => setSubjectError(errorMessage(err, t)),
  });

  const archiveLevel = useMutation({
    mutationFn: (levelId: string) => academicApi.archiveLevel(levelId),
    onSuccess: invalidate,
  });
  const archiveSubject = useMutation({
    mutationFn: (subjectId: string) => academicApi.archiveSubject(subjectId),
    onSuccess: invalidate,
  });

  const levelList = levels.data ?? [];
  const subjectList = subjects.data ?? [];

  return (
    <div className="settings-card">
      <h2 className="page-title">{t('academic.level.title')}</h2>
      {canManage && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (levelName.trim()) createLevel.mutate();
          }}
        >
          {levelError && <Alert tone="danger">{levelError}</Alert>}
          <Field
            label={t('academic.level.name')}
            name="level-name"
            value={levelName}
            onChange={(e) => setLevelName(e.target.value)}
            required
          />
          <Field
            label={t('academic.level.order')}
            name="level-order"
            type="number"
            value={levelOrder}
            onChange={(e) => setLevelOrder(e.target.value)}
            dir="ltr"
          />
          <Button type="submit" variant="primary" loading={createLevel.isPending}>
            {t('academic.level.create')}
          </Button>
        </form>
      )}

      {levels.isPending && <Spinner label={t('common.states.loading')} />}
      {levels.isSuccess && levelList.length === 0 && (
        <EmptyState title={t('academic.level.empty')} />
      )}

      {levelList.map((level) => (
        <div key={level.id} className="detail-row">
          <strong>
            {level.order}. {level.name}
          </strong>
          {canManage && (
            <Button
              variant="ghost"
              loading={archiveLevel.isPending}
              onClick={() => archiveLevel.mutate(level.id)}
            >
              {t('common.actions.archive')}
            </Button>
          )}
        </div>
      ))}

      <h2 className="page-title">{t('academic.subject.title')}</h2>
      {canManage && levelList.length > 0 && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (subjectName.trim() && subjectLevelId) createSubject.mutate();
          }}
        >
          {subjectError && <Alert tone="danger">{subjectError}</Alert>}
          <Field
            label={t('academic.subject.name')}
            name="subject-name"
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            required
          />
          <div className="field">
            <label className="field-label" htmlFor="subject-level">
              {t('academic.level.title')}
            </label>
            <select
              id="subject-level"
              className="field-input"
              value={subjectLevelId}
              onChange={(e) => setSubjectLevelId(e.target.value)}
              required
            >
              <option value="">{t('common.actions.select')}</option>
              {levelList.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
          </div>
          <Field
            label={t('academic.subject.credits')}
            name="subject-credits"
            type="number"
            value={subjectCredits}
            onChange={(e) => setSubjectCredits(e.target.value)}
            dir="ltr"
          />
          <div className="field">
            <label className="field-label" htmlFor="subject-prereq">
              {t('academic.subject.prerequisite')}
            </label>
            <select
              id="subject-prereq"
              className="field-input"
              value={prerequisiteId}
              onChange={(e) => setPrerequisiteId(e.target.value)}
            >
              <option value="">{t('academic.subject.noPrerequisite')}</option>
              {subjectList.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" loading={createSubject.isPending}>
            {t('academic.subject.create')}
          </Button>
        </form>
      )}
      {canManage && levelList.length === 0 && (
        <p className="field-hint">{t('academic.subject.needsLevelFirst')}</p>
      )}

      {subjects.isSuccess && subjectList.length === 0 && (
        <EmptyState title={t('academic.subject.empty')} />
      )}
      {subjectList.map((subject) => (
        <div key={subject.id} className="detail-row">
          <span>
            {subject.name}
            {subject.credits !== null &&
              ` · ${t('academic.subject.creditsCount', { count: subject.credits })}`}
            {subject.prerequisiteSubjectId && (
              <span className="cell-flag">
                {' '}
                · {t('academic.subject.requires')}{' '}
                {subjectList.find((s) => s.id === subject.prerequisiteSubjectId)?.name}
              </span>
            )}
          </span>
          {canManage && (
            <Button
              variant="ghost"
              loading={archiveSubject.isPending}
              onClick={() => archiveSubject.mutate(subject.id)}
            >
              {t('common.actions.archive')}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// --- Teachers ------------------------------------------------------------------------

function TeachersTab({ schoolId, canManage }: { schoolId: string; canManage: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const teachers = useQuery({
    queryKey: ['academic', 'teachers', schoolId],
    queryFn: ({ signal }) => academicApi.listTeachers(schoolId, false, signal),
  });
  const subjects = useQuery({
    queryKey: ['academic', 'subjects', schoolId],
    queryFn: ({ signal }) => academicApi.listSubjects(schoolId, false, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['academic', 'teachers', schoolId] });

  const [person, setPerson] = useState<{ id: string; displayName: string } | null>(null);
  const [specialty, setSpecialty] = useState('');
  const [subjectIds, setSubjectIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      academicApi.createTeacher(schoolId, {
        personId: person!.id,
        specialty: specialty.trim() || null,
        subjectIds,
      }),
    onSuccess: () => {
      setPerson(null);
      setSpecialty('');
      setSubjectIds([]);
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: (teacherId: string) => academicApi.archiveTeacher(teacherId),
    onSuccess: invalidate,
  });

  const list = teachers.data ?? [];
  const subjectList = subjects.data ?? [];

  return (
    <div className="settings-card">
      {canManage && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (person) create.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          {person ? (
            <span className="chip">
              {person.displayName}
              <button type="button" onClick={() => setPerson(null)}>
                ×
              </button>
            </span>
          ) : (
            <PersonPicker
              label={t('academic.teacher.person')}
              placeholder={t('groups.group.choosePerson')}
              onPick={(id, displayName) => setPerson({ id, displayName })}
            />
          )}
          <Field
            label={t('academic.teacher.specialty')}
            name="teacher-specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
          />
          {subjectList.length > 0 && (
            <fieldset className="form-grid">
              <legend>{t('academic.teacher.subjects')}</legend>
              {subjectList.map((subject) => (
                <label key={subject.id} className="field-checkbox">
                  <input
                    type="checkbox"
                    checked={subjectIds.includes(subject.id)}
                    onChange={(event) =>
                      setSubjectIds(
                        event.target.checked
                          ? [...subjectIds, subject.id]
                          : subjectIds.filter((id) => id !== subject.id),
                      )
                    }
                  />
                  {subject.name}
                </label>
              ))}
            </fieldset>
          )}
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!person}>
            {t('academic.teacher.create')}
          </Button>
        </form>
      )}

      {teachers.isPending && <Spinner label={t('common.states.loading')} />}
      {teachers.isSuccess && list.length === 0 && (
        <EmptyState title={t('academic.teacher.empty')} />
      )}
      {list.map((teacher) => (
        <div key={teacher.id} className="detail-row">
          <span>
            {teacher.displayName}
            {teacher.specialty && <span className="cell-flag"> · {teacher.specialty}</span>}
          </span>
          {canManage && (
            <Button
              variant="ghost"
              loading={archive.isPending}
              onClick={() => archive.mutate(teacher.id)}
            >
              {t('common.actions.archive')}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// --- Course sections -------------------------------------------------------------------

function SectionsTab({ schoolId, canManage }: { schoolId: string; canManage: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const sections = useQuery({
    queryKey: ['academic', 'course-sections', schoolId],
    queryFn: ({ signal }) =>
      academicApi.listCourseSections({ schoolId, includeArchived: false, limit: 100 }, signal),
  });
  const subjects = useQuery({
    queryKey: ['academic', 'subjects', schoolId],
    queryFn: ({ signal }) => academicApi.listSubjects(schoolId, false, signal),
  });
  const teachers = useQuery({
    queryKey: ['academic', 'teachers', schoolId],
    queryFn: ({ signal }) => academicApi.listTeachers(schoolId, false, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['academic', 'course-sections', schoolId] });

  const [subjectId, setSubjectId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [term, setTerm] = useState('');
  const [schedule, setSchedule] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      academicApi.createCourseSection({
        subjectId,
        teacherId: teacherId || null,
        term: term.trim(),
        schedule: schedule.trim() || null,
        capacity: capacity ? Number(capacity) : null,
      }),
    onSuccess: () => {
      setTerm('');
      setSchedule('');
      setCapacity('');
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const subjectList = subjects.data ?? [];
  const teacherList = teachers.data ?? [];
  const sectionList = sections.data?.data ?? [];

  return (
    <div className="settings-card">
      {canManage && subjectList.length > 0 && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (subjectId && term.trim()) create.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="field">
            <label className="field-label" htmlFor="section-subject">
              {t('academic.subject.title')}
            </label>
            <select
              id="section-subject"
              className="field-input"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              required
            >
              <option value="">{t('common.actions.select')}</option>
              {subjectList.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="section-teacher">
              {t('academic.teacher.title')}
            </label>
            <select
              id="section-teacher"
              className="field-input"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
            >
              <option value="">{t('academic.courseSection.noTeacher')}</option>
              {teacherList.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.displayName}
                </option>
              ))}
            </select>
          </div>
          <Field
            label={t('academic.courseSection.term')}
            name="section-term"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            required
          />
          <Field
            label={t('academic.courseSection.schedule')}
            name="section-schedule"
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
          />
          <Field
            label={t('academic.courseSection.capacity')}
            name="section-capacity"
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            dir="ltr"
          />
          <Button type="submit" variant="primary" loading={create.isPending}>
            {t('academic.courseSection.create')}
          </Button>
        </form>
      )}
      {canManage && subjectList.length === 0 && (
        <p className="field-hint">{t('academic.courseSection.needsSubjectFirst')}</p>
      )}

      {sections.isPending && <Spinner label={t('common.states.loading')} />}
      {sections.isSuccess && sectionList.length === 0 && (
        <EmptyState title={t('academic.courseSection.empty')} />
      )}
      {sectionList.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('academic.subject.title')}</th>
                <th scope="col">{t('academic.courseSection.term')}</th>
                <th scope="col">{t('academic.enrollment.title')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {sectionList.map((section) => (
                <tr key={section.id}>
                  <td>{section.subjectName}</td>
                  <td dir="ltr">{section.term}</td>
                  <td dir="ltr">
                    {section.enrollmentCount}
                    {section.capacity !== null && ` / ${section.capacity}`}
                  </td>
                  <td>
                    <Link
                      to={`/academic/course-sections/${section.id}`}
                      className="btn"
                      data-variant="ghost"
                    >
                      {t('academic.courseSection.manageRoster')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
